import React from 'react';
import { Box, Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { TOUCH } from '../../theme/tokens';

/**
 * The action row at the foot of a ListCard.
 *
 * Two signals, doing two different jobs:
 *
 *   Saturation says how important — one solid filled button per row, everything else a soft
 *   tint. The product card used to run three saturated gradients side by side, so nothing read
 *   as the main action and every card was a traffic light.
 *
 *   Hue says which action — money is brand blue, discounts are green, settings are neutral.
 *   Vendors learn "the green one is discount" and scan by color before they read the label,
 *   which matters when reading is the slow part. Stripping hue entirely costs that cue; the
 *   fix for a loud card is to drop the saturation, not the color.
 *
 * Tints are alpha over the current surface rather than fixed hexes, so they hold up in dark
 * mode, where a pale-tint hex would glow. Dark mode needs a heavier alpha to register at all.
 *
 * A crowded row (3+) stacks each icon above its label. Beside the label an icon costs ~26px,
 * and at 390px a secondary button only gets ~72px — which "Discount" already fills on its own.
 * Stacking spends height instead of width, which this row has to spare. Roomier rows keep the
 * icon inline, where there is no such pressure.
 *
 * Icons are per-action, not one repeated mark. Four identical pencils would say only "these
 * are buttons", which their fill already says; a distinct icon per action is what a vendor
 * scanning by shape can actually use.
 *
 * @param actions [{ key, label, icon?, onClick, primary?, tone?, hidden?, disabled? }]
 *                `tone`: 'primary' (money) | 'success' (discounts) | 'error' (destructive) |
 *                'neutral' (default — settings, utilities)
 *                `primary: true` is the one solid button. Every button shows its icon; only
 *                the placement changes with row width.
 *                `hidden` entries are dropped — pass the condition rather than filtering at
 *                the call site, so the flex weights below stay correct.
 * @param sx      merged onto the row (spacing only)
 */
export default function CardActions({ actions = [], sx = {} }) {
    const visible = actions.filter((a) => a && !a.hidden);
    if (!visible.length) return null;

    // Only a crowded row needs to divide the width up. One or two buttons stretched edge to
    // edge read as a page-level call to action, which is far too loud for a row in a list —
    // let them hug their labels instead.
    const fill = visible.length >= 3;

    return (
        <Box sx={{ display: 'flex', gap: 0.75, mt: 1, ...sx }}>
            {visible.map((a) => (
                <Button
                    key={a.key}
                    size="small"
                    variant={a.primary ? 'contained' : 'text'}
                    color={a.primary ? 'primary' : 'inherit'}
                    disableElevation
                    disabled={a.disabled}
                    startIcon={!fill ? a.icon : undefined}
                    onClick={a.onClick}
                    sx={(theme) => {
                        const dark = theme.palette.mode === 'dark';
                        const base = {
                            // The primary earns more room: its label is usually the longest and
                            // it is the one a vendor is reaching for.
                            flex: fill ? (a.primary ? 1.5 : 1) : '0 0 auto',
                            minWidth: 0,
                            minHeight: fill ? TOUCH.stacked : TOUCH.dense,
                            px: fill ? 0.5 : 1.5,
                            fontWeight: a.primary ? 700 : 600,
                            whiteSpace: 'nowrap',
                            ...(fill ? {
                                flexDirection: 'column',
                                gap: 0.125,
                                lineHeight: 1.2,
                                py: 0.5,
                                // The stacked icon is decoration next to its own label; sizing
                                // it here keeps it from inheriting the label's font size.
                                '& .CardActions-icon': { fontSize: '1.05rem', display: 'block' },
                            } : {}),
                        };
                        if (a.primary) return base;

                        // Tonal: a wash of the hue over the surface, with text in the readable
                        // end of that hue for the current mode.
                        const tone = a.tone || 'neutral';
                        const hue = tone === 'neutral'
                            ? theme.palette.text.primary
                            : theme.palette[tone].main;
                        return {
                            ...base,
                            bgcolor: alpha(hue, dark ? 0.20 : 0.09),
                            color: tone === 'neutral'
                                ? theme.palette.text.primary
                                : theme.palette[tone][dark ? 'light' : 'dark'],
                            '&:hover': { bgcolor: alpha(hue, dark ? 0.30 : 0.16) },
                        };
                    }}
                >
                    {fill && a.icon
                        ? React.cloneElement(a.icon, { className: 'CardActions-icon' })
                        : null}
                    {a.label}
                </Button>
            ))}
        </Box>
    );
}
